import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Save, ShieldCheck, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/Layout";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Devices } from "@/pages/Devices";

const has = (perms, module, action) => (perms?.[module] || []).includes(action);

const toggle = (perms, module, action) => {
  const current = perms?.[module] || [];
  const next = current.includes(action)
    ? current.filter((a) => a !== action)
    : [...current, action];
  const out = { ...perms };
  if (next.length) out[module] = next;
  else delete out[module];
  return out;
};

export const Matrix = ({ modules, value, onChange, disabled, testPrefix }) => (
  <div className="surface overflow-x-auto" data-testid={`${testPrefix}-matrix`}>
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b bg-muted/40 text-left">
          <th className="sticky left-0 z-10 bg-muted/40 px-3 py-2 font-semibold">Module</th>
          {["view", "create", "edit", "delete", "approve", "export", "view_full"].map((a) => (
            <th key={a} className="px-2 py-2 text-center font-semibold capitalize">
              {a === "view_full" ? "Full no." : a}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {modules.map((m) => (
          <tr key={m.key} className="border-b last:border-0 hover:bg-muted/20">
            <td className="sticky left-0 z-10 bg-background px-3 py-2 font-medium">{m.label}</td>
            {["view", "create", "edit", "delete", "approve", "export", "view_full"].map((a) => {
              const supported = m.actions.some((x) => x.key === a);
              return (
                <td key={a} className="px-2 py-2 text-center">
                  {supported ? (
                    <Checkbox
                      disabled={disabled}
                      data-testid={`${testPrefix}-${m.key}-${a}`}
                      checked={has(value, m.key, a)}
                      onCheckedChange={() => onChange(toggle(value, m.key, a))}
                    />
                  ) : (
                    <span className="text-muted-foreground/40">—</span>
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const RoleEditor = ({ modules, builtin, role, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: role?.name || "",
    description: role?.description || "",
    base_role: role?.base_role || "employee",
    permissions: role?.permissions || {},
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (form.name.trim().length < 3) return toast.error("Role name must be at least 3 characters");
    if (!Object.keys(form.permissions).length) return toast.error("Tick at least one permission");
    setSaving(true);
    try {
      if (role?.id) await api.put(`/roles/custom/${role.id}`, form);
      else await api.post("/roles/custom", form);
      toast.success(role?.id ? "Role updated" : "Role created");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-5xl" data-testid="role-editor-dialog">
      <DialogHeader>
        <DialogTitle>{role?.id ? `Edit role — ${role.name}` : "New custom role"}</DialogTitle>
      </DialogHeader>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Role name">
          <Input
            data-testid="role-name-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Fallback built-in role">
          <Select value={form.base_role} onValueChange={(v) => setForm({ ...form, base_role: v })}>
            <SelectTrigger data-testid="role-base-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {builtin.map((b) => (
                <SelectItem key={b.value} value={b.value}>
                  {b.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Copy from built-in role">
          <Select
            value=""
            onValueChange={(v) => {
              const src = builtin.find((b) => b.value === v);
              setForm({ ...form, permissions: { ...(src?.permissions || {}) } });
              toast.success(`Copied ${src?.label} permissions — adjust as you like`);
            }}
          >
            <SelectTrigger data-testid="role-copy-select">
              <SelectValue placeholder="Start from…" />
            </SelectTrigger>
            <SelectContent>
              {builtin.map((b) => (
                <SelectItem key={b.value} value={b.value}>
                  {b.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="What this role is for" className="sm:col-span-3">
          <Textarea
            rows={2}
            data-testid="role-description-input"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </Field>
      </div>
      <div className="max-h-[45vh] overflow-y-auto">
        <Matrix
          modules={modules}
          value={form.permissions}
          onChange={(permissions) => setForm({ ...form, permissions })}
          testPrefix="role"
        />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} data-testid="role-cancel-button">
          Cancel
        </Button>
        <Button onClick={save} disabled={saving} data-testid="role-save-button" className="gap-1.5">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save role
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

const PeopleTab = ({ modules, builtin, customRoles }) => {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState("");
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadEmployees = useCallback(
    () =>
      api
        .get("/employees")
        .then(({ data }) => setEmployees(data))
        .catch((e) => toast.error(apiError(e))),
    []
  );

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const openEmployee = async (id) => {
    setSelected(id);
    setDetail(null);
    try {
      const { data } = await api.get(`/employees/${id}/permissions`);
      setDetail(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/employees/${selected}/permissions`, {
        role: detail.role,
        role_id: detail.role_id || "",
        extra_permissions: detail.extra_permissions || {},
      });
      setDetail(data);
      toast.success("Permissions saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Choose an employee">
          <Select value={selected} onValueChange={openEmployee}>
            <SelectTrigger data-testid="employee-permission-select">
              <SelectValue placeholder="Select employee" />
            </SelectTrigger>
            <SelectContent>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.name} — {e.role_label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        {detail && (
          <>
            <Field label="Built-in role">
              <Select value={detail.role} onValueChange={(role) => setDetail({ ...detail, role })}>
                <SelectTrigger data-testid="employee-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {builtin.map((b) => (
                    <SelectItem key={b.value} value={b.value}>
                      {b.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Custom role (overrides the built-in list)">
              <Select
                value={detail.role_id || "none"}
                onValueChange={(v) => setDetail({ ...detail, role_id: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="employee-custom-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No custom role</SelectItem>
                  {customRoles.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </>
        )}
      </div>

      {detail ? (
        <>
          <p className="text-xs text-muted-foreground" data-testid="employee-permission-note">
            Extra grants below are added on top of {detail.custom_role_name || detail.role_label}.
            Tick “Contact Numbers → See full numbers” to let this person see complete customer and
            supplier phone numbers.
          </p>
          <div className="max-h-[52vh] overflow-y-auto">
            <Matrix
              modules={modules}
              value={detail.extra_permissions}
              onChange={(extra_permissions) => setDetail({ ...detail, extra_permissions })}
              testPrefix="employee"
            />
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="employee-effective-permissions">
            {Object.entries(detail.effective || {}).map(([m, acts]) => (
              <Badge key={m} variant="outline" className="text-[10px]">
                {m}: {acts.join(", ")}
              </Badge>
            ))}
          </div>
          <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="employee-permission-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save permissions
          </Button>
        </>
      ) : (
        <Empty title="Select an employee to fine-tune their access" />
      )}
    </div>
  );
};

export default function Permissions() {
  const [meta, setMeta] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(
    () =>
      api
        .get("/permissions/matrix")
        .then(({ data }) => setMeta(data))
        .catch((e) => toast.error(apiError(e))),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const modules = useMemo(() => meta?.modules || [], [meta]);

  if (!meta) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        testId="permissions-header"
        title="Permission Matrix"
        subtitle="Owner only — custom roles, per-person access and contact-number visibility"
        actions={
          <Button
            className="gap-1.5"
            data-testid="new-custom-role-button"
            onClick={() => setEditing({})}
          >
            <Plus className="h-4 w-4" /> New custom role
          </Button>
        }
      />
      <div className="space-y-4 p-4 sm:p-6">
        <Tabs defaultValue="roles">
          <TabsList data-testid="permissions-tabs">
            <TabsTrigger value="roles" data-testid="tab-custom-roles">
              Custom roles
            </TabsTrigger>
            <TabsTrigger value="people" data-testid="tab-people">
              People
            </TabsTrigger>
            <TabsTrigger value="builtin" data-testid="tab-builtin">
              Built-in roles
            </TabsTrigger>
            <TabsTrigger value="devices" data-testid="tab-devices">
              Devices & sign-ins
            </TabsTrigger>
          </TabsList>

          <TabsContent value="roles" className="mt-4 space-y-3">
            {meta.custom_roles.length === 0 ? (
              <Empty title="No custom roles yet" hint="Create one to give exact access to a person or team" />
            ) : (
              meta.custom_roles.map((r) => (
                <div
                  key={r.id}
                  className="surface flex flex-wrap items-center justify-between gap-3 p-4"
                  data-testid={`custom-role-row-${r.id}`}
                >
                  <div>
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <ShieldCheck className="h-4 w-4 text-primary" /> {r.name}
                    </div>
                    <p className="text-xs text-muted-foreground">{r.description || "—"}</p>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {Object.entries(r.permissions || {}).map(([m, acts]) => (
                        <Badge key={m} variant="outline" className="text-[10px]">
                          {m}: {acts.length}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`edit-role-button-${r.id}`}
                      onClick={() => setEditing(r)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      data-testid={`delete-role-button-${r.id}`}
                      onClick={async () => {
                        if (!window.confirm(`Delete the role "${r.name}"?`)) return;
                        try {
                          await api.delete(`/roles/custom/${r.id}`);
                          toast.success("Role deleted");
                          load();
                        } catch (e) {
                          toast.error(apiError(e));
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </TabsContent>

          <TabsContent value="people" className="mt-4">
            <PeopleTab modules={modules} builtin={meta.builtin_roles} customRoles={meta.custom_roles} />
          </TabsContent>

          <TabsContent value="devices" className="mt-4">
            <Devices employees={meta.employees || []} />
          </TabsContent>

          <TabsContent value="builtin" className="mt-4 space-y-3">            {meta.builtin_roles.map((b) => (
              <div key={b.value} className="surface p-4" data-testid={`builtin-role-${b.value}`}>
                <div className="text-sm font-semibold">{b.label}</div>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {Object.entries(b.permissions || {}).map(([m, acts]) => (
                    <Badge key={m} variant="outline" className="text-[10px]">
                      {m}: {acts.join(", ")}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing && (
          <RoleEditor
            modules={modules}
            builtin={meta.builtin_roles}
            role={editing}
            onClose={() => setEditing(null)}
            onSaved={load}
          />
        )}
      </Dialog>
    </div>
  );
}
